import React from 'react';
import { getAchievementLevel, getNextAchievement } from '../utils/achievements';
import { Award, Leaf, TreeDeciduous, Sparkles } from 'lucide-react';

// Icon mapping based on achievement level
const getAchievementIcon = (levelName) => {
  switch (levelName) {
    case 'Seedling':
    case 'Sprout':
      return Leaf;
    case 'Sapling':
    case 'Young Tree':
    case 'Oak':
    case 'Elder Oak':
      return TreeDeciduous;
    case 'Ancient':
    case 'Sage':
    case 'Elder Sage':
    case 'Guardian':
    case 'Legend':
      return Sparkles;
    default:
      return Award;
  }
};

export const AchievementBadge = ({ daysFasted, size = 'md', showLabel = false, showProgress = false }) => {
  const level = getAchievementLevel(daysFasted);
  const nextLevel = getNextAchievement(daysFasted);
  const Icon = getAchievementIcon(level.name);

  const sizeClasses = {
    sm: 'w-6 h-6 text-xs',
    md: 'w-8 h-8 text-sm',
    lg: 'w-12 h-12 text-base',
    xl: 'w-16 h-16 text-lg',
  };

  const iconSizes = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-6 w-6',
    xl: 'h-8 w-8',
  };

  return (
    <div className="flex flex-col items-center gap-1">
      <div 
        className={`${sizeClasses[size]} ${level.color} rounded-full flex items-center justify-center text-white font-bold shadow-md border-2 border-white`}
        title={`${level.name} - ${daysFasted} days fasted`}
      >
        {daysFasted}
      </div>
      
      {showLabel && (
        <div className="text-center">
          <div className={`flex items-center gap-1 ${level.textColor} font-medium text-sm`}>
            <Icon className={iconSizes[size === 'xl' ? 'lg' : 'sm']} />
            <span>{level.name}</span>
          </div>
          <p className="text-xs text-muted-foreground">{level.description}</p>
        </div>
      )}

      {showProgress && nextLevel && (
        <div className="w-full mt-2">
          <div className="flex justify-between text-xs text-muted-foreground mb-1">
            <span>{daysFasted} days</span>
            <span>{nextLevel.days} days</span>
          </div>
          <div className="h-2 bg-secondary rounded-full overflow-hidden">
            <div 
              className={`h-full ${nextLevel.color} transition-all duration-500`}
              style={{ 
                width: `${((daysFasted - level.days) / (nextLevel.days - level.days)) * 100}%` 
              }}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1 text-center">
            {nextLevel.daysRemaining} days to {nextLevel.name}
          </p>
        </div>
      )}
    </div>
  );
};

export const AchievementBadgeInline = ({ daysFasted }) => {
  const level = getAchievementLevel(daysFasted);
  
  return (
    <span 
      className={`inline-flex items-center gap-1 ${level.color} text-white text-xs font-bold px-2 py-0.5 rounded-full`}
      title={`${level.name} - ${daysFasted} days fasted`}
    >
      {daysFasted}
    </span>
  );
};

export const AchievementCard = ({ daysFasted }) => {
  const level = getAchievementLevel(daysFasted);
  const nextLevel = getNextAchievement(daysFasted);
  const Icon = getAchievementIcon(level.name);

  return (
    <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 border border-border/50">
      <div className="flex items-center gap-4">
        <div className={`w-16 h-16 ${level.color} rounded-full flex items-center justify-center shadow-lg`}>
          <Icon className="h-8 w-8 text-white" />
        </div>
        <div className="flex-1">
          <h4 className={`font-display text-xl ${level.textColor}`}>{level.name}</h4>
          <p className="text-sm text-muted-foreground">{level.description}</p>
          <p className="text-lg font-bold text-foreground mt-1">
            {daysFasted} {daysFasted === 1 ? 'day' : 'days'} fasted
          </p>
        </div>
      </div>
      
      {nextLevel && (
        <div className="mt-4 pt-4 border-t border-border/50">
          <div className="flex justify-between text-sm text-muted-foreground mb-2">
            <span>Progress to {nextLevel.name}</span>
            <span>{nextLevel.daysRemaining} days to go</span>
          </div>
          <div className="h-3 bg-secondary rounded-full overflow-hidden">
            <div 
              className={`h-full ${nextLevel.color} transition-all duration-500`}
              style={{ 
                width: `${((daysFasted - level.days) / (nextLevel.days - level.days)) * 100}%` 
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AchievementBadge;
